import type {
  ApplyMandatePreviewRequest,
  ApplyMandatePreviewResponse,
  ApplyMandateResponse,
  ChooseMandateTemplateRequest,
  GoalMandateComparisonResponse,
  GoalMandateStateResponse,
  MandateScope,
  SaveMandateDecisionRequest,
  SaveMandateDecisionResponse,
  UpdateMandateFromPortfolioResponse,
} from "@repo/shared/types";
import { del, get, post } from "../_client";

const BASE = "/internal/flow-instances";

/**
 * QL-375 — the goal's mandate check. `scope: "portfolio"` judges one
 * portfolio on its own weights (informational under goal-level blocking).
 */
export function getMandateState(
  instanceId: string,
  goalId: string,
  scope: MandateScope = "goal",
  portfolioId?: string | null,
) {
  const params = new URLSearchParams({ scope });
  if (scope === "portfolio" && portfolioId) params.set("portfolioId", portfolioId);
  return get<GoalMandateStateResponse>(
    `${BASE}/${instanceId}/goals/${goalId}/mandate-state?${params.toString()}`,
  );
}

/** QL-375 group 8 — screen 12: the bank's current mandate beside the proposed one (null = one side only). */
export function getMandateComparison(instanceId: string, goalId: string) {
  return get<GoalMandateComparisonResponse>(
    `${BASE}/${instanceId}/goals/${goalId}/mandate-comparison`,
  );
}

/** QL-375 group 5 — accept with a note / block / clear (`decision: null`). */
export function saveMandateDecision(
  instanceId: string,
  goalId: string,
  request: SaveMandateDecisionRequest,
) {
  return post<SaveMandateDecisionResponse>(
    `${BASE}/${instanceId}/goals/${goalId}/mandate-decision`,
    request satisfies SaveMandateDecisionRequest,
  );
}

export function clearMandateDecision(instanceId: string, goalId: string) {
  return del<SaveMandateDecisionResponse>(`${BASE}/${instanceId}/goals/${goalId}/mandate-decision`);
}

// ── Group 6 — the picker and Apply ───────────────────────────────────────────

/** The Mandate selection section's Save (QL-677; screen 09's pick before): the goal's mandate. */
export function selectMandateTemplate(instanceId: string, goalId: string, templateId: string) {
  return post<SaveMandateDecisionResponse>(
    `${BASE}/${instanceId}/goals/${goalId}/mandate/select-template`,
    { templateId } satisfies ChooseMandateTemplateRequest,
  );
}

/** QL-677 — the Mandate selection section's Clear: removes the template selection. */
export function clearMandateTemplate(instanceId: string, goalId: string) {
  return del<SaveMandateDecisionResponse>(
    `${BASE}/${instanceId}/goals/${goalId}/mandate/selection`,
  );
}

/** Screen 08 — what Apply would write to this empty custom portfolio. */
export function previewApplyMandate(
  instanceId: string,
  portfolioId: string,
  /** A build (an amount, optionally a template) or, QL-730 A2, a model switch. */
  request: ApplyMandatePreviewRequest,
) {
  return post<ApplyMandatePreviewResponse>(
    `${BASE}/${instanceId}/portfolios/${portfolioId}/mandate/apply-preview`,
    request,
  );
}

/**
 * Screen 08 — write the plan: one holding per creatable line, all or none.
 * QL-730 A2 — on a model portfolio `modelPortfolioId` switches it to that
 * matching model portfolio instead (the amount is ignored).
 */
export function applyMandate(
  instanceId: string,
  portfolioId: string,
  /** A build (the previewed amount and template) or a model switch (the picked match). */
  request: ApplyMandatePreviewRequest,
) {
  return post<ApplyMandateResponse>(
    `${BASE}/${instanceId}/portfolios/${portfolioId}/mandate/apply`,
    request,
  );
}

// ── Group 7 — the portfolio's own decision and Update from portfolio ───────

/** Portfolio-level blocking: accept with a note / block on ONE portfolio. */
export function savePortfolioMandateDecision(
  instanceId: string,
  portfolioId: string,
  request: SaveMandateDecisionRequest,
) {
  return post<SaveMandateDecisionResponse>(
    `${BASE}/${instanceId}/portfolios/${portfolioId}/mandate-decision`,
    request satisfies SaveMandateDecisionRequest,
  );
}

export function clearPortfolioMandateDecision(instanceId: string, portfolioId: string) {
  return del<SaveMandateDecisionResponse>(
    `${BASE}/${instanceId}/portfolios/${portfolioId}/mandate-decision`,
  );
}

/** QL-677 — "Create mandate based on this portfolio": the portfolio becomes the mandate's basis. */
export function setMandateBasisPortfolio(instanceId: string, portfolioId: string) {
  return post<UpdateMandateFromPortfolioResponse>(
    `${BASE}/${instanceId}/portfolios/${portfolioId}/mandate/set-basis`,
  );
}

/** QL-664 — write the goal's combined year-1 allocation as its proposed (fixed) mandate. */
export function updateMandateFromGoal(instanceId: string, goalId: string) {
  return post<UpdateMandateFromPortfolioResponse>(
    `${BASE}/${instanceId}/goals/${goalId}/mandate/update-from-goal`,
  );
}
